import { splitLeadingHeading } from "@/components/storyboard/text/utils/splitLeadingHeading";
import { titledBodyMarkdown } from "@/components/storyboard/text/utils/titledBodyMarkdown";
import type { TextTitledBodyData } from "@/types/storyboard";

export type SpeechLine = {
  type: "speech";
  speaker: string;
  side: "left" | "right";
  content: string;
};

export type ProseLine = {
  type: "prose";
  content: string;
};

export type DialoguePart = SpeechLine | ProseLine;

export type ParsedDialogue = {
  title: string | null;
  parts: DialoguePart[];
};

export const SPEECH_RE = /^\*\*([^*]+):\*\*\s*([\s\S]*)/;

// Shared — used by both structured and legacy payloads
const splitIntoParagraphs = (text: string): string[] =>
  text.split(/\n\n+/).filter((paragraph) => paragraph.trim());

const mapParagraphsToDialogueParts = (paragraphs: string[]): DialoguePart[] => {
  const speakers: string[] = [];

  return paragraphs.map((paragraph) => {
    const match = paragraph.match(SPEECH_RE);
    if (match) {
      const speaker = match[1].trim();
      if (!speakers.includes(speaker)) speakers.push(speaker);
      return {
        type: "speech",
        speaker,
        side: speakers.indexOf(speaker) === 0 ? "left" : "right",
        content: match[2].trim(),
      } satisfies SpeechLine;
    }
    return { type: "prose", content: paragraph } satisfies ProseLine;
  });
};

// Structured payload — `{ title, body }`.
// Title comes from JSON. Markdown is parsed as-is (no heading promotion).
const parseStructuredDialogue = (data: TextTitledBodyData): ParsedDialogue => ({
  title: data.title,
  parts: mapParagraphsToDialogueParts(
    splitIntoParagraphs(titledBodyMarkdown(data))
  ),
});

// Legacy fallback — older courses send text: string
// Title is scraped from ## / standalone **Title** inside the markdown string.
const extractLegacyDialogueTitle = (
  text: string
): { title: string | null; body: string } => {
  const { title: headingTitle, body: afterHeading } = splitLeadingHeading(text);
  const paragraphs = splitIntoParagraphs(afterHeading);

  if (headingTitle) {
    return { title: headingTitle, body: afterHeading };
  }

  const firstParagraph = paragraphs[0];
  const boldTitleMatch = firstParagraph?.match(/^\*\*([^*]+)\*\*$/);
  if (!boldTitleMatch) {
    return { title: null, body: text };
  }

  return {
    title: boldTitleMatch[1].trim(),
    body: paragraphs.slice(1).join("\n\n"),
  };
};

const parseLegacyDialogueString = (text: string): ParsedDialogue => {
  const { title, body } = extractLegacyDialogueTitle(text);

  return {
    title,
    parts: mapParagraphsToDialogueParts(splitIntoParagraphs(body)),
  };
};

// Entry point — TextDialogue branches here once on payload shape
export const resolveDialogueContent = (
  data: TextTitledBodyData | string
): ParsedDialogue =>
  typeof data === "string"
    ? parseLegacyDialogueString(data)
    : parseStructuredDialogue(data);

// Exported for focused unit tests (not used by the component directly).
export const parseStructuredDialogueBody = (body: string): DialoguePart[] =>
  mapParagraphsToDialogueParts(splitIntoParagraphs(body));

export const parseLegacyDialogue = parseLegacyDialogueString;
